import { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useQueryState } from 'nuqs';
import {
  PLAYS_GROUP_BY_NONE,
  RECENT_START_DATE_CHIP_ID,
  detachEditedStartDateChip,
  parsePlaysGroupBy,
  refreshRecentStartDateChip,
  type FlowPlaysRow,
  type PlaysGroupBy,
} from '@shared/flow/plays-view';
import {
  PLAYS_DEFAULT_PRESET_ID,
  PLAYS_PRESETS,
  PLAYS_VIEW_PARAM,
  parsePlaysSort,
} from '@shared/flow/plays-table';
import {
  fetchLauncherCampaign,
  launcherCampaignQueryKey,
  patchCampaignDetails,
  type PatchCampaignFields,
} from '../../../../../../mini-apps/_shared/launcher-campaign-schema';
import { wrapThrowingApiFetch } from '../../../../../../mini-apps/_shared/fetch-error';
import { flowUrlParams } from '../../../../../../mini-apps/_shared/url-params';
import { useApi } from '../../../lib/api';
import { useUserControlsContext } from '../../../contexts/UserControlsContext';
import type { FilterGroup } from '@shared/flow/filter';
import type { FlowView } from '@shared/flow/view';
import type { FlowTableProps } from '../types';
import { useFlowListLayout } from '../../../hooks/useFlowLayouts';
import { useFlowLayoutAwareMachine } from '../flow-layouts/useFlowLayoutAwareMachine';
import { useViewStateMachine } from '../views/useViewStateMachine';
import { useFlowViewState, useFlowViewStatePersist, type FlowViewStateStore } from '../views/useViewState';
import { FLOW_PLAYS_QUERY_KEY } from './keys';
import { useFlowPlays } from './hooks';
import { usePlaysTableConfig } from './playsTableConfig';
import { orderPlaysForTable } from './playsTableRows';
import { useDebouncedValue } from '../useDebouncedValue';

/**
 * Headless Plays-list model (plan `plans/2026-09-28-008-terra-design-v2-
 * plays-signals-plan.md` S2-U2, precedent `account/useAccountTableModel.tsx`
 * / `contact/useContactTableModel.tsx`) — everything the plays list mount
 * needs: the saved-views machine, `?play=` selection, the transient debounced
 * search, the plays query, and the "View Details"/"Manage Event"
 * campaign-sidebar controls. `PlaysSubtab` (V1) and `PlaysTable` (V2) are
 * thin renderers over this hook's return value.
 *
 * Saved views (plan `plans/2026-11-03-001-plays-saved-views-and-favorites-
 * plan.md` U6): the list runs on `useViewStateMachine` like Accounts —
 * entity `play`, the built-in "Upcoming plays" preset (`PLAYS_PRESETS`), its
 * own `play` view-state row (D2; the legacy `plays` row is abandoned in place)
 * and its own `?view_play=` URL param, so the People roster on the same page
 * keeps the bare `?view=` and the two machines never read each other's param.
 *
 * See `PlaysSubtab`'s module doc for the behavioural spec this hook carries
 * (selection vs. filtering, the `?play=` `history: 'replace'` contract,
 * search staying transient, the D9 deep-link header name fallback, and the
 * U8 campaign-sidebar gating).
 */

/**
 * D3 — the rolling start-date chip (`RECENT_START_DATE_CHIP_ID`) stores a
 * MATERIALIZED date. The machine's baseline is refreshed by `adaptOnOpen`,
 * but a restored working state bypasses `adaptOnOpen`, so without this the
 * restored working filter would keep the stale date while the baseline moved
 * to today — a phantom "Modified" every day after the row was written.
 *
 * Returns the store UNCHANGED (same object) when there is nothing to refresh:
 * no row, no working state, or no rolling chip in it. Only
 * `restored.workingState.filter` changes; every other field passes through.
 */
export function withRefreshedStartDateChip(store: FlowViewStateStore): FlowViewStateStore {
  const restored = store.restored;
  const workingState = restored?.workingState;
  if (!restored || !workingState) return store;
  if (!workingState.filter.some(group => group.id === RECENT_START_DATE_CHIP_ID)) return store;
  return {
    ...store,
    restored: {
      ...restored,
      workingState: { ...workingState, filter: refreshRecentStartDateChip(workingState.filter) },
    },
  };
}

/**
 * The machine's array-shaped `groupBy` → the single group key
 * `orderPlaysForTable` understands. Plays stays single-level by design, so
 * only the first level counts; `[]` is ungrouped (`'none'`) and an unknown key
 * (a retired one on an old saved view) falls back to the `play_type` default.
 */
export function playsGroupKeyOf(levels: string[]): PlaysGroupBy {
  return levels.length === 0 ? PLAYS_GROUP_BY_NONE : parsePlaysGroupBy(levels[0]);
}

/** D3 — `adaptOnOpen` for BOTH kinds: a saved view that carries the rolling chip keeps its id, so its stored date goes stale too. */
function refreshViewStartDate(view: FlowView): FlowView {
  return { ...view, filter: refreshRecentStartDateChip(view.filter) };
}

/** Stable empty-rows identity for the pre-load render, so the config's option memo doesn't churn. */
const NO_PLAYS: FlowPlaysRow[] = [];

/**
 * A11 — the plays list must not push the People section off the fold, so it
 * keeps the `max-h-[380px]` internal scroller the deleted accordion had.
 * Passed as `FlowTable`'s `maxHeightClassName` rather than wrapped in an
 * outer `overflow` box: the table's own scroll container is what the sticky
 * `<thead>` is positioned against, so a second scroller outside it would
 * unpin the header.
 */
export const PLAYS_MAX_HEIGHT_CLS = 'max-h-[380px]'; // `PLAYS_ROW_MAX_HEIGHT_CLS` (PlaysTable.tsx) derives from this 380px

/**
 * A18 — a FLOOR for the plays scroll card, alongside the `max-h-[380px]` cap.
 * Without it, collapsing every group shrank the card to its header and
 * snapped the People section up the page. Blank space beneath the last row
 * when groups are collapsed is intended.
 */
const PLAYS_MIN_HEIGHT_CLS = 'min-h-[280px]';

/** A17 — matches the People search box's own debounce (`ContactFlowTable.tsx`), so the two boxes on this page feel identical. */
const SEARCH_DEBOUNCE_MS = 300;

/** Module-scope no-ops so the props' identity is stable across renders. */
function noop(): void {}

/** Where a play's type is unknown until the plays list resolves it (D9): event invites are the only type with a membership source today. */
const DEFAULT_PLAY_TYPE = 'event_invite';

export function usePlaysListModel() {
  // Server-side working-state store, per `(user, 'play')` (D2) — the state
  // key equals the view entity so "Set as default" (`loadDefaultViewId`)
  // finds it. Wrapped so a restored rolling chip carries today's date (D3);
  // the wrapper keeps the store's identity when there is nothing to refresh.
  const rawViewState = useFlowViewState('play');
  const viewState = useMemo(() => withRefreshedStartDateChip(rawViewState), [rawViewState]);

  const rawMachine = useViewStateMachine({
    entity: 'play',
    presets: PLAYS_PRESETS,
    defaultPresetId: PLAYS_DEFAULT_PRESET_ID,
    // Plays has no role defaults, so only the stored view state gates the
    // first resolution — the plays fetch never fires with unseeded filters.
    ready: viewState.isRestored,
    // D3 — the baseline (and an opened view's working state) gets today's
    // window on every open, presets and saved views alike. Module-level, so
    // its identity is stable without a `useCallback`.
    adaptOnOpen: refreshViewStartDate,
    viewState,
    // Risk 4 — the People roster on the same page owns the bare `?view=`.
    viewParam: PLAYS_VIEW_PARAM,
  });

  // D6 — a Plays view can pin a list layout, exactly as on Accounts. The
  // wrapped machine reads an unsaved Layout-menu pick as "Modified", drops it
  // on Reset / view switch and commits it on Save / Create view.
  const layout = useFlowListLayout('plays', { viewPin: rawMachine.activeView.flowLayout ?? null });
  const machine = useFlowLayoutAwareMachine(rawMachine, 'plays', layout);

  // Write-back half: every dirty-relevant change, debounced ~1s. No
  // `initialPayloadIsDirty`: the gate opens on the machine's first
  // resolution, whose working state IS the restored row, so the first
  // payload is never echoed back (D3's refreshed chip date is written with
  // the next real edit, not on load).
  useFlowViewStatePersist({
    stateKey: 'play',
    enabled: machine.isReady,
    activeViewId: machine.activeViewId,
    working: machine.working,
  });

  const { filter: filters, groupLogic, sort, groupBy, columns, columnWidths } = machine.working;
  const { setGroupBy } = machine;
  /** Which row's "Actions ▾" menu is open — one at a time. */
  const [openMenuPlayId, setOpenMenuPlayId] = useState<string | null>(null);

  // U8 — "View Details"/"Manage Event" gate + the in-page campaign sidebar.
  const { isMarketingUser, isAdmin } = useUserControlsContext();
  const canManageEvents = isMarketingUser || isAdmin;
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  const [detailPanel, setDetailPanel] = useState<{ campaignId: string; mode: 'view' | 'edit' } | null>(null);
  const openDetails = useCallback((play: FlowPlaysRow, mode: 'view' | 'edit') => {
    setDetailPanel({ campaignId: play.play_id, mode });
  }, []);
  const closeDetailPanel = useCallback(() => setDetailPanel(null), []);
  const detailCampaignId = detailPanel?.campaignId ?? null;
  const campaignQuery = useQuery({
    queryKey: launcherCampaignQueryKey(detailCampaignId ?? ''),
    enabled: detailCampaignId !== null,
    // See the module doc's U8 note — `fetchLauncherCampaign` is written for
    // the mini-app boundary's THROWING `apiFetch`, not core Terra's
    // Response-returning one, hence `wrapThrowingApiFetch(apiFetch)` rather
    // than `apiFetch` directly.
    queryFn: () => fetchLauncherCampaign(wrapThrowingApiFetch(apiFetch), detailCampaignId!),
  });
  /** The campaign-sidebar's own save — invalidates both the detail and the plays list, then closes. */
  const save = useCallback((fields: PatchCampaignFields): Promise<void> => {
    const campaignId = detailPanel?.campaignId;
    if (!campaignId) return Promise.resolve();
    return patchCampaignDetails(wrapThrowingApiFetch(apiFetch), campaignId, fields).then(() => {
      void queryClient.invalidateQueries({ queryKey: launcherCampaignQueryKey(campaignId) });
      void queryClient.invalidateQueries({ queryKey: FLOW_PLAYS_QUERY_KEY });
      closeDetailPanel();
    });
  }, [detailPanel, apiFetch, queryClient, closeDetailPanel]);

  /**
   * D6 — the selected play, in the URL (nuqs only). `history: 'replace'` so
   * clicking through plays doesn't stack a history entry per click.
   * Selecting/Back touch ONLY `?play=` (D3 — see `PlaysSubtab`'s module
   * doc); there is no `?view=` to clear any more.
   */
  const [playId, setPlayId] = useQueryState('play', flowUrlParams.play.withOptions({ history: 'replace' }));
  const selectPlay = useCallback((id: string) => { void setPlayId(id); }, [setPlayId]);
  const backToAllPeople = useCallback(() => { void setPlayId(null); }, [setPlayId]);

  /**
   * A17 — the Search box's text lives in PLAIN React state, deliberately
   * outside the machine's working state.
   *
   * Round 2 wired it to a `play_name contains` filter chip
   * (`chip-play-name-search`), which made every committed keystroke a
   * working-state mutation: it marked the view dirty ("Modified" / "Reset" /
   * "Save as new view") and `useFlowViewStatePersist` PUT it into the
   * view-state row — so a rep's throwaway search came back on reload as if
   * they had saved it as a filter. Search is a transient READ, so it rides
   * its own request param (`?search=`) and touches nothing persistent.
   *
   * This also makes Codex round-2 #2 moot by construction: there is no
   * restore path that can clobber the text (the view-state row knows nothing
   * about it) and no persist baseline that can swallow it, so typing while
   * the view-state GET is still in flight is simply safe.
   *
   * `useDraftState` is NOT wanted here: this is not a draft seeded from a
   * server value that re-renders could reset — it has no seed at all. A bare
   * `useState` with no reseed effect is the correct shape (and is what the
   * SSE-re-render rule actually asks for; the rule forbids `useState` +
   * reseed effect, not `useState`).
   */
  const [searchText, setSearchText] = useState('');
  // Debounced before it reaches the query key, so a burst of keystrokes is
  // ONE request rather than one per character — each of which costs three
  // Terra-Signals count aggregates server-side (Codex round-1 #6). The
  // request that does get superseded is now genuinely cancelled, via React
  // Query's signal (Codex round-2 #6, `useFlowPlays`).
  const debouncedSearch = useDebouncedValue(searchText, SEARCH_DEBOUNCE_MS);

  // Gated on the machine's first resolution (D7), so the first request
  // already carries the restored / preset filters.
  const playsQuery = useFlowPlays({ filters, groupLogic, search: debouncedSearch }, { enabled: machine.isReady });
  const plays = playsQuery.data?.plays ?? NO_PLAYS;

  const selectedPlay = playId === null ? undefined : plays.find(p => p.play_id === playId);

  /**
   * D9 — when `?play=` names a play the plays table's current page lacks (a
   * deep link, or a play filtered out of the list), fall back to the SAME
   * launcher campaign fetch the "View Details"/"Manage Event" sidebar already
   * makes, rather than the deleted members route's `play` envelope.
   */
  const headerCampaign = useQuery({
    queryKey: launcherCampaignQueryKey(playId ?? ''),
    enabled: playId !== null && selectedPlay === undefined,
    meta: { suppressErrorToast: true },
    queryFn: () => fetchLauncherCampaign(wrapThrowingApiFetch(apiFetch), playId!),
  });
  const playName = selectedPlay?.name ?? headerCampaign.data?.name ?? 'this play';
  const playType = selectedPlay?.play_type ?? DEFAULT_PLAY_TYPE;

  const config = usePlaysTableConfig({
    plays,
    selectedPlayId: playId,
    onSelectPlay: selectPlay,
    openMenuPlayId,
    setOpenMenuPlayId,
    canManageEvents,
    onOpenDetails: openDetails,
  });

  // `parsePlaysSort` guards a saved view / restored row whose sort names a
  // column this table has no comparator for (a retired key): rows fall back
  // to the default order rather than silently rendering payload order.
  const rows = useMemo(
    () => orderPlaysForTable(plays, playsGroupKeyOf(groupBy), parsePlaysSort(sort)),
    [plays, groupBy, sort],
  );
  // Plays stays single-level by design (`orderPlaysForTable`), so only the
  // first level a picker hands back is kept.
  const onGroupByChange = useCallback((levels: string[]) => setGroupBy(levels.slice(0, 1)), [setGroupBy]);
  const { openView, setFilters } = machine;
  // The filter popover keeps a group's id when a rep edits it, so an edited
  // rolling start-date chip would still carry the managed id and be clobbered
  // by the daily refresh on the next open. Detach it before it reaches the machine.
  const onFiltersChange = useCallback(
    (groups: FilterGroup[]) => setFilters(detachEditedStartDateChip(groups)),
    [setFilters],
  );
  const onViewChange = useCallback((id: string) => { void openView(id); }, [openView]);

  /**
   * `emptyState` is deliberately NOT here (review F9): it is JSX, and the
   * two renderers of this model draw it in two different design systems —
   * V1 `PlaysSubtab` in Terra's own classes, V2 `PlaysTable` in Glaze
   * tokens. A model that ships one of them forces the other surface to
   * render the wrong one, which is exactly how V1's `dark:text-nightshade-*`
   * classes ended up inside a `shell-next/` ledger. Omitted from the type so
   * a renderer cannot forget it silently.
   *
   * Also omitted: `dirtyBox` (the bound machine renders its own dirty box —
   * "Modified" / Reset / Save — through `viewMachine`) and `toolbarTrailing`
   * (never set here).
   */
  const tableProps: Omit<FlowTableProps<FlowPlaysRow>, 'dirtyBox' | 'toolbarTrailing' | 'emptyState'> = {
    viewMachine: machine,
    views: machine.presets,
    activeViewId: machine.activeViewId,
    onViewChange,
    config,
    rows,
    // The plays query is disabled until the machine resolves, and a
    // disabled query is not "loading" — so the gate counts as loading too.
    isLoading: !machine.isReady || playsQuery.isLoading,
    isRefetching: playsQuery.isPlaceholderData || (playsQuery.isFetching && !playsQuery.isLoading),
    error: playsQuery.isError ? (playsQuery.error?.message ?? 'Failed to load plays') : null,
    // `LedgerTable` renders a Retry button whenever this is supplied; V1 `FlowTable` ignores it.
    onRetry: () => { void playsQuery.refetch(); },
    // A26 — no paging on this surface YET. Round 3 mapped these onto
    // the withheld-Gong count ("Load Gong flows (660)"), which is
    // deleted: that button offered rows the active filters would have
    // thrown away the moment they arrived (Codex round-3 #1). When Gong
    // flows return as ordinary plays they page through the server-side
    // LIMIT/cursor these props were built for, not a special case.
    hasMore: false,
    onLoadMore: noop,
    isLoadingMore: false,
    sort,
    onSortChange: machine.setSort,
    filters,
    onFiltersChange,
    // A20 — the REAL outer combinator, persisted in the `play`
    // view-state row (and on a saved view) and sent to the route, which compiles the campaign
    // SQL with it and applies the same logic across groups in the TS
    // evaluator for singleton/Gong rows.
    groupLogic,
    onGroupLogicChange: machine.setGroupLogic,
    // A17 — the box renders the rep's keystrokes instantly; only the
    // debounced value reaches the request.
    search: searchText,
    onSearchChange: setSearchText,
    // The machine's own array (`[]` = ungrouped), passed through by
    // reference: `FlowTable`'s `groupCounts` memo keys on its identity, and
    // the machine only replaces it on a real grouping change.
    groupBy,
    onGroupByChange,
    // Round-1 fix R12 / D-H — the picker must offer exactly the ONE
    // level this surface can honour. `maxGroupLevels={1}` restores the
    // pre-U4 flow: open, click a field, done — plus a "Clear grouping" row.
    maxGroupLevels: 1,
    visibleColumns: columns,
    onVisibleColumnsChange: machine.setColumns,
    // 2026-09-22 plan — the Plays list opts into `FlowTable`'s existing,
    // already-shared column-resize mechanism (the SAME
    // `columnWidths`/`onColumnWidthsChange` prop pair `ContactFlowTable`
    // threads from `useViewStateMachine`), rather than porting
    // Playhouse's bespoke `ResizeHandle`. Widths never dirty a view and
    // survive Reset / view switches (the machine's own rule).
    columnWidths,
    onColumnWidthsChange: machine.setColumnWidths,
    totalLoaded: rows.length,
    maxHeightClassName: PLAYS_MAX_HEIGHT_CLS,
    minHeightClassName: PLAYS_MIN_HEIGHT_CLS,
  };

  return {
    tableProps,
    /** The Plays saved-views machine (layout-aware) — V2 mounts its pane / toolbar from this. */
    machine,
    /** The `plays` list layout (D6) — follows the open view's pin. */
    layout,
    playId,
    selectPlay,
    backToAllPeople,
    selectedPlay,
    playName,
    playType,
    /** U8's "View Details"/"Manage Event" campaign-sidebar controls. */
    detail: {
      panel: detailPanel,
      campaign: campaignQuery.data,
      close: closeDetailPanel,
      save,
    },
    plays,
    searchText,
    canManageEvents,
    openDetails,
  };
}

